import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { Prefill } from "./LeadForm";
import { VARIANTS, type EnquiryType } from "./content";
import LeadFormDialog from "./LeadFormDialog";
import type { LeadFormRequest } from "./LeadFormPanel";

// Imported statically on purpose. The landing pack's splitChunks group in
// config/webpack/clientWebpackConfig.js only claims chunks named `landing`, so an `import()` here
// lands in the application's aggregate `components` chunk — several megabytes for a marketing page
// to fetch on a CTA click. The dialog is only mounted once a CTA is pressed either way.
const LeadFormContext = createContext<((request: LeadFormRequest) => void) | null>(null);

// Throws rather than defaulting to a no-op: a CTA rendered outside the provider would otherwise be
// a button that silently does nothing, with no sign of it until someone reports a dead CTA.
export function useLeadForm() {
  const open = useContext(LeadFormContext);

  if (!open) throw new Error("useLeadForm must be used inside <LeadFormProvider>");

  return open;
}

export function LeadFormProvider({ children }: { children: React.ReactNode }) {
  const [request, setRequest] = useState<LeadFormRequest | null>(null);

  // Closing the dialog unmounts the form. Held in a ref so a visitor who pressed Esc by accident
  // gets their answers back, without every keystroke re-rendering the page behind the dialog.
  const draft = useRef<Prefill>({});
  const remember = useCallback((values: Prefill) => {
    draft.current = values;
  }, []);

  // The CTA's own prefill wins: pressing the estimator's button again should carry the estimate it
  // is showing now, not the number the visitor typed over it last time.
  const open = useCallback(
    (next: LeadFormRequest) =>
      setRequest({ ...next, prefill: { ...draft.current, ...next.prefill } }),
    []
  );
  const close = useCallback(() => setRequest(null), []);

  // The CTAs Rails renders sit outside this island, so a crawler reads the offer without running
  // JavaScript. Each is a real link to /speak-to-us that this upgrades to the dialog, the way the
  // ERB navbar's anchors are handled from the document rather than from a handler on the element.
  useEffect(() => {
    const upgrade = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const trigger = (event.target as Element | null)?.closest<HTMLElement>("[data-lead-form]");
      const enquiryType = trigger?.dataset.leadForm as EnquiryType | undefined;
      if (!enquiryType || !(enquiryType in VARIANTS)) return;

      event.preventDefault();
      open({ enquiryType });
    };

    document.addEventListener("click", upgrade);
    return () => document.removeEventListener("click", upgrade);
  }, [open]);

  return (
    <LeadFormContext.Provider value={open}>
      {children}
      {request ? <LeadFormDialog request={request} onDraft={remember} onClose={close} /> : null}
    </LeadFormContext.Provider>
  );
}
